import { DocBreadcrumbs } from '../components/DocBreadcrumbs';
import { DocPagination } from '../components/DocPagination';
import { PagePurpose } from '../components/PagePurpose';

const palette = {
  heroDark: '#0F6B7C',
  heroLight: '#4A97AC',
  border: '#D4E2F4',
  surface: '#F4F7FC',
  badge: '#E0F2F1',
};

const statBadges = ['49,836 zones', '10 scenarios', '384 MB data', '8 municipalities'];

const challengeFacts = [
  { label: 'Client', value: 'Stedendriehoek (8 municipalities)' },
  { label: 'Question', value: 'Where should 120,000 new homes go by 2040?' },
  {
    label: 'Complexity',
    value: '133 locations · 10 variants · OV infrastructure scenarios',
  },
];

type StepDetail = {
  heading: string;
  description?: string;
  items?: string[];
};

type StepImage = {
  src: string;
  alt: string;
  caption?: string;
};

type StepDownload = {
  label: string;
  href: string;
  description?: string;
  fileSize?: string;
};

type DataFlowStep = {
  title: string;
  summary: string;
  details?: StepDetail[];
  badges?: string[];
  images?: StepImage[];
  downloads?: StepDownload[];
  why: string;
};

const dataFlowSteps: DataFlowStep[] = [
  {
    title: 'Step 1 · Christian (Spatial Planner) - Scenario Definition',
    summary:
      'Christian defines policy scenarios with the spatial team: 3 main variants (Regional Addition 43.5k, Local Strengthening 41.5k, Urban Transformation 60k homes) plus subvariants. He translates them into two standardized inputs Roland needs: an Excel file with the numbers and a shapefile showing where growth happens.',
    details: [
      {
        heading: 'What Christian provides',
        items: [
          'Excel file `StedendriehoekinputDashboard2.xlsx` - 133 development locations with housing units, jobs, and inhabitants plus ten scenario columns (baseline + nine variants).',
          'Shapefile `Stedendriehoekplannen.shp/.dbf/.shx` - 130 polygon geometries in RD New projection; attributes match the Excel columns and mark where each growth program sits.',
        ],
      },
      {
        heading: 'Source references',
        items: [
          'Anchored in the Stedendriehoek Variantenstudie (17 Oct 2025) which sets the 48,000-home ambition: 26,000 homes committed for 2025-2034, 4,000 still open for 2030-2034, and 10,000 for 2035-2050.',
          'Excel + shapefile trace the same 133 Woondeal locations (Apeldoorn 10.4k homes, Deventer 6.9k, Zutphen 2.5k, etc.) so Christian can prove compliance when presenting to the Ontwerp-Nota Ruimte partners.',
        ],
      },
      {
        heading: 'Why this step matters',
        description:
          "Spatial planners speak in maps and policies. Christian converts regional strategies into structured data Roland's calculation engine can digest. The Excel-Shapefile pair is SB's standard dashboard input-proven in Brainport MRE, Twente, and now Stedendriehoek.",
      },
    ],
    badges: ['.xlsx Excel', '.shp Shapefile', '133 locations defined'],
    downloads: [
      {
        label: 'Download Excel input',
        href: '/downloads/sb470/StedendriehoekinputDashboard2.xlsx',
        description: 'Baseline + nine variants for 133 locations',
        fileSize: '45 KB · .xlsx',
      },
      {
        label: 'Download shapefile (ZIP)',
        href: '/downloads/sb470/Stedendriehoekplannen_shapefile.zip',
        description: 'RD New polygons for 130 development areas',
        fileSize: 'ZIP · .shp/.dbf/.shx',
      },
    ],
    images: [
      {
        src: '/images/projects/sb470/001a_Totaal_Inwoners_Woningen_Stedendriehoek_Hoofdvarianten.png',
        alt: "Christian's 10 scenario variants - total housing and population targets per variant",
        caption: "Christian's 10 scenario variants - total housing and population targets per variant",
      },
    ],
    why:
      "Christian's structured inputs make spatial policy questions machine-readable so Roland can calculate proximity impacts without guessing what each variant means.",
  },
  {
    title: 'Step 2 · Roland (Data Specialist) - QlikView Processing',
    summary:
      "Roland loads Christian's inputs into his urbanization calculation model, which integrates WLO 2050 government scenarios. The model calculates proximity indices-how urban each of the 49,836 zones becomes under every variant-combining population density, job accessibility, and OV connectivity. Output: a 46 MB QlikView dashboard plus 48 TXT files with raw results.",
    details: [
      {
        heading: "What Roland's calculation does",
        items: [
          'Inputs: Christian\'s Excel + Shapefile + WLO 2050 scenarios (PBL/CBS national forecasts).',
          'Processing: Proximity Index methodology-population density within 500m/1,500m rings, job accessibility at 2/4/6/8/10 km, OV connectivity (walk/bike to stations), facilities coverage, housing density.',
          'Calculation scope: 49,836 zones × 10 variants = 498,360 records, each with multi-metric outputs.',
        ],
      },
      {
        heading: "Roland's four deliverables",
        items: [
          '1. QlikView dashboard (.qvf, 46 MB) - interactive tool on the Windows server (credentials in Bitwarden) where the team filters by variant, municipality, and indicator.',
          '2. PNG exports (66 files) - bar charts, trend lines, modal split diagrams, and housing density distributions pulled straight from the dashboard.',
          '3. Proximity maps (22 styled maps, PDF + PNG) - color-coded urbanization levels per zone, one map per variant.',
          '4. TXT files (48 data files, 384 MB) - tab-separated outputs with WKT geometries, led by D011_Data_AP.txt (148 MB) plus OV stations, employment, and facilities datasets.',
        ],
      },
    ],
    badges: [
      '.qvf QlikView (46 MB)',
      '.txt WKT data (48 files · 384 MB)',
      '.png Charts (66 files)',
      '.pdf/.png Maps (22 files)',
      'Windows server access · Bitwarden credentials',
    ],
    images: [
      {
        src: '/images/projects/sb470/Kaart_1_Nabijheidsindex_Scen_1.png',
        alt: "Roland's proximity map - Variant 1 urbanization levels across 49,836 zones",
        caption: "Roland's proximity map - Variant 1 urbanization levels across 49,836 zones",
      },
      {
        src: '/images/projects/sb470/005_Dichtheid_woningvoorraad_Stedendriehoek_Hoofdvarianten.png',
        alt: 'Housing density comparison chart - QlikView export showing 3 main variants',
        caption: 'Housing density comparison chart - QlikView export showing 3 main variants',
      },
      {
        src: '/images/projects/sb470/CSb_CheatSheet_Totalen_en_Selecties_Varianten_Hoofd_en_subvarianten.png',
        alt: 'Variant cheat sheet summarizing totals and selection filters straight from the QlikView dashboard',
        caption: "Roland's cheat sheet - totals and selection logic for ten variants in one glance",
      },
      {
        src: '/images/projects/sb470/003a_Aantal_km_in_lange_trips_per_vervoerwijze_Stedendriehoek_Hoofdvarianten.png',
        alt: 'Modal split diagram showing long-trip kilometers per transport mode across main variants',
        caption: 'Modal split impact - kilometers traveled per mode for the three hoofdvarianten',
      },
    ],
    why:
      "Roland's methodology mirrors the national NERM/Proximity Index framework, so municipalities trust the evidence. The TXT files with WKT geometries are the bridge to GIS-Giovanni can load them into Python/QGIS without opening QlikView.",
  },
  {
    title: 'Step 3 · Giovanni (Data Scientist) - Analysis & MapHub Preparation',
    summary:
      "Giovanni loads Roland's TXT files (with WKT geometries) into Python/GeoPandas for exploratory data analysis. Eight Jupyter notebooks document validation checks, municipality summaries, hotspot detection, and indicator correlations. Final output: 11 GeoPackages styled for MapHub delivery, so the team can access results in a browser or QGIS without touching TXT files.",
    details: [
      {
        heading: 'Inputs',
        items: [
          "Roland's 48 TXT files with WKT geometries, especially D011_Data_AP.txt for the full zone-by-variant matrix.",
          'Supplemental OV stations, employment, and facilities TXT exports for enrichment.',
        ],
      },
      {
        heading: 'Processing (8 Jupyter notebooks)',
        items: [
          '02_variant_insights.ipynb - municipality summaries and growth distribution.',
          '03_variant_comparison.ipynb - cross-variant indicator analysis.',
          '04_spatial_hotspots.ipynb - high-growth zone identification.',
          '05_qvf_metadata.ipynb - QlikView variable extraction.',
          '06_indicator_maps.ipynb - styled choropleth generation.',
          '07_qvf_driven_analysis.ipynb - dashboard-aligned metrics.',
          '08_formula_verification.ipynb - calculation validation.',
          'Internal intake notebook - EDA scaffolding + QA checks before publishing.',
        ],
      },
      {
        heading: 'EDA focus',
        items: [
          'Validate completeness (all 49,836 zones accounted for) and cross-check Christian\'s totals.',
          'Flag anomalies such as zones with impossible density.',
          'Generate municipality-level summaries and test color schemes requested by the client.',
        ],
      },
      {
        heading: 'Outputs',
        items: [
          'GeoPackages (11 files, one per variant: 5500 baseline + 5510-5533 scenarios) with 81 attributes, styled for MapHub/QGIS.',
          'Analysis figures (50+ PNG files) including OV accessibility categories, population growth hotspots, and housing density distributions.',
        ],
      },
    ],
    badges: ['.gpkg GeoPackage (11 files)', '.ipynb Jupyter notebooks (8)', '.png Analysis figures (50+)', 'Python · GeoPandas · Matplotlib'],
    images: [
      {
        src: '/images/projects/sb470/5510_variant_1_optelsom_43_5k_ov_category.png',
        alt: "Giovanni's OV accessibility analysis - Variant 5510 showing service categories and new station locations",
        caption: "Giovanni's OV accessibility analysis - Variant 5510 showing service categories and new station locations",
      },
      {
        src: '/images/projects/sb470/5510_variant_1_optelsom_43_5k_population.png',
        alt: 'Population growth visualization - color-coded by density levels for Variant 5510',
        caption: 'Population growth visualization - color-coded by density levels for Variant 5510',
      },
      {
        src: '/images/projects/sb470/5510_variant_1_optelsom_43_5k_housing.png',
        alt: "Housing capacity map from Giovanni's Jupyter notebooks showing units per hectare",
        caption: 'Housing density styling exported from notebook 06_indicator_maps.ipynb',
      },
      {
        src: '/images/projects/sb470/5510_variant_1_optelsom_43_5k_employment.png',
        alt: 'Employment accessibility map comparing zones with strong job proximity',
        caption: 'Employment proximity layers derived in 02_variant_insights.ipynb',
      },
      {
        src: '/images/projects/sb470/5510_variant_1_optelsom_43_5k_development.png',
        alt: 'Development footprint map confirming all 133 locations match Christian\'s scenario inputs',
        caption: 'Development footprint QA exported from notebook 04_spatial_hotspots.ipynb',
      },
    ],
    why:
      "Roland's TXT files are machine-readable but not browsable. Giovanni's GeoPackages are the last-mile format-MapHub (maphub.studiobereikbaar.nl) serves them to browsers, and Laura/Younes can open them in QGIS for detailed analysis. The notebooks form an audit trail anyone can rerun.",
  },
  {
    title: 'Step 4 · Team Access - Multi-Level Delivery',
    summary:
      "The team accesses results at their skill level. Non-GIS colleagues use our self-hosted MapHub (maphub.studiobereikbaar.nl) — click a link, explore variants, no installs. Power users run the MapHub QGIS plugin to sync GeoPackages. For coordination sessions, Roland's 66 PNG charts and 22 maps stay presentation-ready, and the QlikView server handles live 'what-if' questions.",
    details: [
      {
        heading: 'Three access paths',
        items: [
          '1. MapHub (self-hosted) browser — Christian, Clifford, Manus, and clients open maphub.studiobereikbaar.nl, toggle layers, view attributes, zoom to municipalities, and compare variants without touching the 384 MB dataset.',
          '2. QGIS + MapHub plugin (power users) — Laura and Hugo authenticate with the self-hosted server, download GeoPackages with all 81 attributes, run spatial queries, restyle maps, or export to Shapefile/GeoJSON.',
          '3. QlikView server - Roland, Giovanni, and credentialed teammates log into the Windows server (Bitwarden credentials), filter by municipality/year/variant, and export extra charts during meetings.',
        ],
      },
    ],
    badges: ['🌐 MapHub Self-Hosted', '🗺️ QGIS Plugin', '📊 QlikView Server'],
    images: [],
    why:
      "One calculation run, three delivery formats. MapHub (self-hosted) handles 'show me quick,' QGIS handles 'I need to analyze deeper,' and QlikView handles 'let's explore together.' Accessible by design — meet users where their skills are.",
  },
];

const resultMetrics = [
  'Urbanization levels (proximity index, six categories)',
  'Job accessibility by distance bands (0-2 km, 2-5 km, 5-10 km, 10 km+)',
  'Housing density (units per hectare) and supply saturation',
  'OV service quality (walk/bike access to stations, four tiers)',
  'Neighborhood facilities coverage (shopping, green, amenities within 750-800 m)',
  'Modal split (car/bike/transit share of trips)',
];

const resultVariants = [
  { id: '5500', description: 'Baseline 2022 (current situation)' },
  { id: '5510', description: 'Variant 1 - Regional addition (43.5k homes across 8 municipalities)' },
  { id: '5512', description: 'Variant 1b - Trend scenario matching natural growth' },
  { id: '5513', description: 'Variant 1c - Alternative distribution for Variant 1' },
  { id: '5520', description: 'Variant 2 - Local strengthening (41.5k homes in existing centers)' },
  { id: '5522', description: 'Variant 2b - New OV opportunities with full station wishlist' },
  { id: '5523', description: 'Variant 2c - Alternative local focus' },
  { id: '5524', description: 'Variant 2d - Concentrated local growth (scaled version)' },
  { id: '5530', description: 'Variant 3 - Urban transformation (60k homes, high density)' },
  { id: '5532', description: 'Variant 3b - Maximum urban intensification' },
  { id: '5533', description: 'Variant 3c - Alternative urban configuration' },
];

const timelineEntries = [
  { label: 'Oct 17', detail: 'Christian provides Excel + Shapefile inputs.' },
  { label: 'Oct 24-25', detail: 'Roland runs QlikView calculations and exports TXT/PNG/PDF outputs.' },
  { label: 'Oct 28-31', detail: 'Giovanni performs EDA and packages 11 GeoPackages for MapHub.' },
  { label: 'Nov 6', detail: 'Regional stakeholder meeting with results demoed live.' },
  { label: 'Nov 13', detail: 'Public presentation day using MapHub + PNG storytelling set.' },
];

const demonstrationChecklist = [
  'SB\'s complete dashboard capability - Excel input to interactive delivery in two weeks.',
  'Government data integration - WLO 2050 scenarios ensure policy alignment.',
  'Multi-format delivery - Browser, QGIS, and QlikView serve different user needs.',
  'Laagdrempelig by design - Non-GIS colleagues can explore without software barriers.',
  'Reproducible methodology - Jupyter notebooks document every analysis step.',
  'Scalable approach - Same workflow applies to any regional urbanization study.',
];

const techStack = [
  { label: 'Input preparation', value: 'Excel + QGIS (Christian) - structured scenarios + RD New polygons.' },
  { label: 'Calculation engine', value: 'QlikView Desktop with WLO 2050 data (Roland).' },
  { label: 'Analysis pipeline', value: 'Python · GeoPandas · Matplotlib - eight notebooks + validation scripts (Giovanni).' },
  { label: 'Delivery platforms', value: 'MapHub self-hosted (maphub.studiobereikbaar.nl, browser + QGIS plugin) + QlikView server for live exploration.' },
  { label: 'Access control', value: 'Bitwarden for Windows server credentials + MapHub SB Microsoft SSO.' },
  {
    label: 'Data volumes',
    value:
      'Input: 1 Excel (45 KB) + 1 Shapefile (130 polygons) · Processing: WLO 2050 data for 49,836 zones · Output: 46 MB QlikView + 384 MB TXT + 11 GeoPackages.',
  },
  { label: 'Repository', value: 'SB470-Stedendriehoek_Verstedelijkingsstrategie (internal project).' },
];

export function ProjectShowcasePage() {
  return (
    <div className="space-y-8">
      <DocBreadcrumbs currentTitle="Project Showcase" />

      <PagePurpose
        title="One rollout, start to finish"
        audience="All team members - see how data, methodology, and tools come together on a real project"
        description="A walkthrough of the SB470 Stedendriehoek project, Christian → Roland → Giovanni → team. This is one concrete rollout — an example of the workflow in practice, not a claim that the platform is finished."
        badge="Case study"
      />

      <section
        className="rounded-3xl text-white shadow-xl"
        style={{
          background: `linear-gradient(135deg, ${palette.heroDark} 0%, ${palette.heroLight} 100%)`,
        }}
      >
        <div className="p-8 space-y-6">
          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
            <span>Project Showcase</span>
            <span className="rounded-full bg-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em]">
              Completed November 2025
            </span>
          </div>
          <h1 className="text-4xl font-bold leading-tight">
            Real Implementation: SB470 Stedendriehoek Urbanization Strategy
          </h1>
          <p className="max-w-3xl text-lg leading-relaxed text-white/90">
            A full end-to-end run - from policy question to ready-to-present maps - showing how Studio Bereikbaar
            keeps advanced urbanization analysis accessible for colleagues without a GIS background. It's one
            rollout among many, not proof the platform is done — but it's a good picture of how the pieces fit.
          </p>
          <div className="flex flex-wrap gap-3 text-sm font-semibold">
            {statBadges.map((badge) => (
              <span key={badge} className="rounded-full bg-white/20 px-4 py-2">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-8 shadow-sm" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">The Challenge</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {challengeFacts.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border p-4"
              style={{ borderColor: palette.border, backgroundColor: palette.surface }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</p>
              <p className="mt-2 text-base text-sb-black">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-8 shadow-sm space-y-6" style={{ borderColor: palette.border }}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Data flow</p>
          <h2 className="mt-2 text-3xl font-semibold text-sb-black">Four steps from policy question to delivery</h2>
          <p className="mt-2 text-sm text-sb-dark-grey">
            Christian → Roland → Giovanni → the wider team. Real files, real people, and a workflow the municipalities can audit.
          </p>
        </div>
        <div className="space-y-6">
          {dataFlowSteps.map((step) => (
            <div
              key={step.title}
              className="rounded-2xl border p-6 space-y-4 bg-white"
              style={{ borderColor: palette.border }}
            >
              <div className="flex flex-col gap-2">
                <h3 className="text-xl font-semibold text-sb-black">{step.title}</h3>
                <p className="text-sm text-sb-dark-grey">{step.summary}</p>
              </div>
              {step.badges && step.badges.length > 0 && (
                <div className="flex flex-wrap gap-3 text-xs font-semibold text-sb-black/80">
                  {step.badges.map((badge) => (
                    <span
                      key={badge}
                      className="rounded-full px-3 py-1"
                      style={{ backgroundColor: palette.badge, border: `1px solid ${palette.border}` }}
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              )}
              {step.details && step.details.length > 0 && (
                <div className="space-y-4">
                  {step.details.map((detail) => (
                    <div key={detail.heading} className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{detail.heading}</p>
                      {detail.description && <p className="text-sm text-sb-dark-grey">{detail.description}</p>}
                      {detail.items && (
                        <ul className="space-y-2 text-sm text-sb-dark-grey">
                          {detail.items.map((item) => (
                            <li key={item} className="flex gap-2">
                              <span
                                className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full"
                                style={{ backgroundColor: palette.heroDark }}
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {step.downloads && step.downloads.length > 0 && (
                <div className="flex flex-wrap gap-3 text-sm font-semibold">
                  {step.downloads.map((download) => (
                    <a
                      key={download.href}
                      href={download.href}
                      download
                      className="inline-flex flex-col rounded-xl border px-4 py-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                      style={{ borderColor: palette.border, backgroundColor: palette.surface }}
                    >
                      <span className="text-sb-black">{download.label}</span>
                      {download.description && (
                        <span className="text-xs font-normal text-sb-dark-grey">{download.description}</span>
                      )}
                      {download.fileSize && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          {download.fileSize}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              )}
              {step.images && step.images.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                  {step.images.map((image) => (
                    <figure
                      key={image.src}
                      className="rounded-2xl border bg-white p-3"
                      style={{ borderColor: palette.border }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full rounded-xl border object-cover"
                        style={{ borderColor: palette.border }}
                        loading="lazy"
                      />
                      <figcaption className="mt-2 text-xs text-gray-500">{image.caption ?? image.alt}</figcaption>
                    </figure>
                  ))}
                </div>
              )}
              <p className="text-sm font-semibold text-sb-black">
                Why it matters:{' '}
                <span className="font-normal text-sb-dark-grey">{step.why}</span>
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-8 shadow-sm space-y-6" style={{ borderColor: palette.border }}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Real results</p>
          <h2 className="mt-2 text-3xl font-semibold text-sb-black">Real Results - What the Team Delivered</h2>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <div
            className="rounded-2xl border p-6"
            style={{ borderColor: palette.border, backgroundColor: palette.surface }}
          >
            <h3 className="text-lg font-semibold text-sb-black">Metrics calculated</h3>
            <ul className="mt-3 space-y-2 text-sm text-sb-dark-grey">
              {resultMetrics.map((metric) => (
                <li key={metric} className="flex gap-2">
                  <span className="mt-0.5 text-sb-black">•</span>
                  <span>{metric}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border p-6" style={{ borderColor: palette.border }}>
            <h3 className="text-lg font-semibold text-sb-black">Variants analyzed</h3>
            <p className="mt-3 text-sm text-sb-dark-grey">
              Same numbering as Roland's dashboard, so anyone can cross-check figures instantly.
            </p>
            <div className="mt-4 space-y-2 text-sm text-sb-dark-grey">
              {resultVariants.map((variant) => (
                <div key={variant.id} className="flex gap-3">
                  <span
                    className="rounded-full border px-3 py-1 text-xs font-semibold uppercase"
                    style={{ borderColor: palette.border, backgroundColor: palette.surface }}
                  >
                    {variant.id}
                  </span>
                  <span className="flex-1">{variant.description}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border p-6" style={{ borderColor: palette.border, backgroundColor: palette.surface }}>
            <h3 className="text-lg font-semibold text-sb-black">Timeline delivered</h3>
            <ul className="mt-3 space-y-3 text-sm text-sb-dark-grey">
              {timelineEntries.map((entry) => (
                <li key={entry.label}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{entry.label}</p>
                  <p className="mt-1">{entry.detail}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border bg-white p-8 shadow-sm space-y-4" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">What this demonstrates</h2>
        <ul className="space-y-3 text-sm text-sb-dark-grey">
          {demonstrationChecklist.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-0.5 text-lg text-green-600">✅</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-3xl border bg-white p-8 shadow-sm space-y-4" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Technical stack</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {techStack.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border p-4"
              style={{ borderColor: palette.border, backgroundColor: palette.surface }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</p>
              <p className="mt-2 text-sm text-sb-black">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      <DocPagination currentPageId="project-showcase" />
    </div>
  );
}
